import { FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { isNumericField } from "./preselectFieldMatching";

// Canonicalise a selected value for signature comparison. Numeric-typed fields
// collapse formatting differences (trailing zeros, "2026" vs "2026.000…" — see
// isNumericField for why they are the same query). Every value is lowercased:
// the resolver matches querystring values case-insensitively (itemMatchesValue)
// and SharePoint FilterValue comparison is itself case-insensitive, so a raw
// value differing from the matched item only by letter case ("balans" vs
// "Balans") is the SAME query — comparing it case-sensitively fired a redundant
// corrective re-fetch (a visible second load).
const canonicalSignatureValue = (field: any, value: string | undefined): string => {
  const raw = value ?? "";
  if (isNumericField(field) && raw.trim() !== "") {
    const numeric = Number(raw);
    if (!Number.isNaN(numeric)) {
      return String(numeric);
    }
  }
  return raw.toLowerCase();
};

/**
 * Per-field multiset of selected values, canonicalised (sorted) so comparison is
 * order-independent. This is exactly what the grid query is built from, so two
 * section sets produce the same grid result iff these signatures are equal. The
 * reconcile compares the fast (schema-synthesized) signature with the reconciled
 * one to decide whether a corrective re-fetch is required.
 */
export const selectedValueSignature = (sections: FilterSection[]): string => {
  const byField = new Map<string, string[]>();
  (sections ?? []).forEach(section => {
    const realFieldName = section?.filterField?.RealFieldName ?? "";
    (section?.items ?? [])
      .filter(item => item.selected)
      .forEach(item => {
        const values = byField.get(realFieldName) ?? [];
        values.push(canonicalSignatureValue(section?.filterField, item.value));
        byField.set(realFieldName, values);
      });
  });

  const canonical = Array.from(byField.entries())
    .map(([field, values]) => [field, [...values].sort()] as [string, string[]])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

  return JSON.stringify(canonical);
};
